import { useCallback, useEffect, useMemo, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  type PaymentMethod,
  type StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { capitalizeFirstLetter, type IconName } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import type { UseStripePaymentProps } from '../../types';
import { useNotifyStripeError } from '../useNotifyStripeError';
import {
  getCreditCardIcon,
  removeEndingIn,
  useLabelAndIconByType,
} from './helpers';

export const useStripePayment = (props: UseStripePaymentProps) => {
  const { isIntentConfirmationEnabled, paymentMethods, stripeIntentConfig } =
    props;

  const { track } = useTelemetry();
  const trackStripeUsage = useCallback(
    (payload: unknown) => {
      track('checkout.stripe-usage', payload);
    },
    [track],
  );

  const notifyStripeError = useNotifyStripeError(trackStripeUsage);

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripe = useStripe();
  const elements = useElements();

  // ─── Utils ─────────────────────────────────────────────────────────────────

  const convertTypeToLabelAndIcon = useLabelAndIconByType();

  // ─── State ─────────────────────────────────────────────────────────────────

  const [createdPaymentMethod, setCreatedPaymentMethod] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedPaymentIcon, setSelectedPaymentIcon] =
    useState<IconName>('IconCreditCard');

  const isGooglePay = selectedPaymentMethod === GOOGLE_PAY;
  const isApplePay = selectedPaymentMethod === APPLE_PAY;
  const isLink = selectedPaymentMethod === LINK;
  const isCashApp = selectedPaymentMethod === CASH_APP;

  // ─── Payment Method Creation ───────────────────────────────────────────────

  const createPaymentMethod = useCallback(async () => {
    try {
      if (!stripe || !elements) {
        notifyStripeError();
        return;
      }

      const { error: submitError } = await elements.submit();

      if (submitError) {
        notifyStripeError(submitError.message);
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (error) {
        notifyStripeError(error.message);
        return;
      }

      setCreatedPaymentMethod(paymentMethod.id);

      return paymentMethod;
    } catch (error) {
      notifyStripeError((error as Error).message);
    }
  }, [elements, notifyStripeError, stripe]);

  // ─── Handle Payment Change ─────────────────────────────────────────────────

  const handlePaymentElementChange = useMemo(
    () =>
      getPaymentMethodChangeHandler({
        paymentMethods,
        convertTypeToLabelAndIcon,
        setCreatedPaymentMethod,
        setSelectedPaymentMethod,
        setSelectedPaymentIcon,
        createPaymentMethod,
      }),
    [convertTypeToLabelAndIcon, createPaymentMethod, paymentMethods],
  );

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) {
      return;
    }

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, handlePaymentElementChange]);

  // ─── Confirm Callback ──────────────────────────────────────────────────────

  const confirmStripe = useCallback(async () => {
    if (!stripe || !elements) {
      return { status: 'failed', error: 'Stripe not initialized' };
    }

    if (createdPaymentMethod) {
      trackStripeUsage({ event: 'confirming_with_previous_payment_method' });

      return { status: 'completed', paymentMethodId: createdPaymentMethod };
    }

    if (isIntentConfirmationEnabled && !isGooglePay) {
      trackStripeUsage({ event: 'confirming_payment_intent', selectedPaymentMethod }); // prettier-ignore

      const { error: submitError } = await elements.submit();

      if (submitError) {
        return { status: 'failed', error: submitError.message };
      }

      // Maps the stripe-react-native interface to web.
      return new Promise((resolve) => {
        stripeIntentConfig?.confirmHandler?.(
          {} as never,
          false,
          async ({ clientSecret = '' }) => {
            const result = await stripe.confirmPayment({
              elements,
              clientSecret,
              redirect: redirectCondition,
              confirmParams: { return_url: defaultRedirect },
            });

            if (result.error) {
              resolve({ status: 'failed', error: result.error.message });
              return;
            }

            resolve({ status: 'completed' });
          },
        );
      });
    }

    const paymentMethod = await createPaymentMethod();

    if (!paymentMethod) {
      return { status: 'failed', error: 'No payment method created' };
    }

    trackStripeUsage({ event: 'confirming_with_created_payment_method' });
    return { status: 'completed', paymentMethodId: paymentMethod.id };
  }, [
    stripe,
    elements,
    createdPaymentMethod,
    isIntentConfirmationEnabled,
    isGooglePay,
    createPaymentMethod,
    trackStripeUsage,
    selectedPaymentMethod,
    stripeIntentConfig,
  ]);

  return {
    isGooglePay,
    isApplePay,
    isLink,
    isCashApp,
    selectedPaymentMethod,
    selectedPaymentIcon,
    confirmStripe,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Handles changes to the payment method, setting state as needed.
 */
const getPaymentMethodChangeHandler = (params: PaymentMethodChangeHandler) => {
  const {
    paymentMethods,
    convertTypeToLabelAndIcon,
    setCreatedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentIcon,
    createPaymentMethod,
  } = params;

  return async (event: StripePaymentElementChangeEvent) => {
    setCreatedPaymentMethod('');

    const id = event.value.payment_method?.id;
    const savedPaymentMethod = paymentMethods.find((pm) => pm.id === id);

    // Customer selected a card that is already saved in Stripe.
    if (savedPaymentMethod?.description && savedPaymentMethod?.cardType) {
      setSelectedPaymentMethod(removeEndingIn(savedPaymentMethod.description));
      setSelectedPaymentIcon(getCreditCardIcon(savedPaymentMethod.cardType));
      return;
    }

    // Customer is adding a new card.
    if (event.value.type === 'card' && event.complete) {
      const paymentMethod = await createPaymentMethod();

      if (!paymentMethod?.card) return;

      const { brand, last4 } = paymentMethod.card;

      const label = `${capitalizeFirstLetter(brand)} ${last4}`;

      setSelectedPaymentMethod(label);
      setSelectedPaymentIcon(getCreditCardIcon(brand.toUpperCase()));

      return;
    }

    // Customer selected a wallet payment type.
    if (event.value.type) {
      const { label, icon } = convertTypeToLabelAndIcon(event.value.type);
      setSelectedPaymentMethod(label);
      setSelectedPaymentIcon(icon);
    }
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentMethodChangeHandler = {
  paymentMethods: UseStripePaymentProps['paymentMethods'];
  convertTypeToLabelAndIcon: ReturnType<typeof useLabelAndIconByType>;
  setCreatedPaymentMethod: (value: string) => void;
  setSelectedPaymentMethod: (value: string) => void;
  setSelectedPaymentIcon: (value: IconName) => void;
  createPaymentMethod: () => Promise<PaymentMethod | undefined>;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GOOGLE_PAY = 'Google Pay';
const APPLE_PAY = 'Apple Pay';
const LINK = 'Link';
const CASH_APP = 'Cash App';
const redirectCondition = 'if_required' as const;
const defaultRedirect = window.location.origin;
